import React, { useRef, useEffect, useState } from 'react';
import QRCode from 'qrcode.react';
import { AddressResult } from '../types';
import { generateGoogleMapsUrl } from '../api/addressSearch';
import { readAddress } from '../utils/speech';
import { trackSpeechSynthesis, trackQRCodeInteraction } from '../hooks/useAnalytics';
import './AddressDisplay.css';

interface AddressDisplayProps {
    address: AddressResult | null;
    onClose?: () => void;
}



const AddressDisplay: React.FC<AddressDisplayProps> = ({ address, onClose }) => {
    const addressDisplayRef = useRef<HTMLDivElement>(null);
    const [isReadingEnglish, setIsReadingEnglish] = useState(false);
    const [isReadingChinese, setIsReadingChinese] = useState(false);
    const [copyFeedback, setCopyFeedback] = useState<'english' | 'chinese' | null>(null);
    const [zoomedAddress, setZoomedAddress] = useState<'english' | 'chinese' | null>(null);
    const [activeTab, setActiveTab] = useState<'chinese' | 'english'>('chinese');

    useEffect(() => {
        if (address && addressDisplayRef.current) {
            // Focus the address display container when an address is displayed
            addressDisplayRef.current.focus();
            // Scroll into view smoothly
            addressDisplayRef.current.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'nearest' 
            });
        }
    }, [address]);



    const handleReadEnglishAddress = async () => {
        if (isReadingEnglish || !address) return;
        
        setIsReadingEnglish(true);
        try {
            await readAddress(
                address.addressEn || address.address,
                address.nameEn,
                address.districtEn,
                'en'
            );
            // Track successful speech synthesis
            trackSpeechSynthesis('en', true);
        } catch (error) {
            console.error('Failed to read English address:', error);
            // Track failed speech synthesis
            trackSpeechSynthesis('en', false);
            
            // Show user-friendly error message
            alert('Unable to read English address. Please check your browser\'s speech synthesis support.');
        } finally {
            setIsReadingEnglish(false);
        }
    };

    const handleReadChineseAddress = async () => {
        if (isReadingChinese || !address) return;
        
        setIsReadingChinese(true);
        try {
            await readAddress(
                address.addressZh || address.address,
                address.nameZh,
                address.districtZh,
                'zh'
            );
            // Track successful speech synthesis
            trackSpeechSynthesis('zh', true);
        } catch (error) {
            console.error('Failed to read Chinese address:', error);
            // Track failed speech synthesis
            trackSpeechSynthesis('zh', false);
            
            // Show user-friendly error message
            alert('Unable to read Chinese address. Please check your browser\'s speech synthesis support.');
        } finally {
            setIsReadingChinese(false);
        }
    };

    const handleCopyEnglishAddress = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!address) return;
        
        // Format: Name, Address, District
        const fullAddress = address.districtEn 
            ? `${address.nameEn}, ${address.addressEn}, ${address.districtEn}`
            : `${address.nameEn}, ${address.addressEn}`;
        
        try {
            await navigator.clipboard.writeText(fullAddress);
            setCopyFeedback('english');
            setTimeout(() => setCopyFeedback(null), 2000);
            console.log('English address copied:', fullAddress);
        } catch (error) {
            console.error('Failed to copy English address:', error);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = fullAddress;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopyFeedback('english');
            setTimeout(() => setCopyFeedback(null), 2000);
        }
    };

    const handleCopyChineseAddress = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!address) return;
        
        // Format: Name, Address, District
        const fullAddress = address.districtZh 
            ? `${address.nameZh}, ${address.addressZh}, ${address.districtZh}`
            : `${address.nameZh}, ${address.addressZh}`;
        
        try {
            await navigator.clipboard.writeText(fullAddress);
            setCopyFeedback('chinese');
            setTimeout(() => setCopyFeedback(null), 2000);
            console.log('Chinese address copied:', fullAddress);
        } catch (error) {
            console.error('Failed to copy Chinese address:', error);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = fullAddress;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopyFeedback('chinese');
            setTimeout(() => setCopyFeedback(null), 2000);
        }
    };

    const handleZoomAddress = (language: 'english' | 'chinese') => {
        setZoomedAddress(language);
    };

    const handleCloseZoom = () => {
        setZoomedAddress(null);
    };

    const handleClosePopup = () => {
        if (onClose) {
            onClose();
        }
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            handleClosePopup();
        }
    };



    if (!address) {
        return null;
    }

    // Generate Google Maps URLs for both languages
    const englishAddress = address.addressEn || address.address;
    const chineseAddress = address.addressZh || address.address;
    const englishGoogleMapsUrl = generateGoogleMapsUrl(englishAddress);
    const chineseGoogleMapsUrl = generateGoogleMapsUrl(chineseAddress);

    return (
        <>
            <div className="address-popup-overlay" onClick={handleOverlayClick}>
                <div 
                    className="address-popup-modal" 
                    ref={addressDisplayRef}
                    tabIndex={-1}
                >
                    <div className="address-popup-header">
                        <h2 className="address-popup-title">Address Details</h2>
                        <button 
                            className="address-popup-close"
                            onClick={handleClosePopup}
                            aria-label="Close address popup"
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                    <div className="address-popup-content">
                        <div className="address-tabs">
                            <div className="tabs-header">
                                <button
                                    className={`tab-button ${activeTab === 'chinese' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('chinese')}
                                >
                                    中文
                                </button>
                                <button
                                    className={`tab-button ${activeTab === 'english' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('english')}
                                >
                                    English
                                </button>
                            </div>
                            <div className="tabs-content">
                                {activeTab === 'chinese' && (
                                    <div className="address-section">
                                        <div className="section-header">
                                            <h3 className="section-title">中文</h3>
                                    <div className="section-actions">
                                        <button
                                            className={`speech-button ${isReadingChinese ? 'reading' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleReadChineseAddress();
                                            }}
                                            aria-label="Read Chinese address"
                                            title="Read Chinese address"
                                        >
                                            <svg className="speech-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                                                <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
                                            </svg>
                                        </button>
                                        <button
                                            className={`copy-button ${copyFeedback === 'chinese' ? 'copied' : ''}`}
                                            onClick={handleCopyChineseAddress}
                                            aria-label="Copy Chinese address"
                                            title="Copy Chinese address"
                                        >
                                            <svg className="copy-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                                            </svg>
                                        </button>
                                        <button
                                            className="zoom-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleZoomAddress('chinese');
                                            }}
                                            aria-label="Zoom Chinese address"
                                            title="Zoom Chinese address"
                                        >
                                            <svg className="zoom-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <circle cx="11" cy="11" r="8"/>
                                                <path d="m21 21-4.35-4.35"/>
                                                <path d="M11 8v6"/>
                                                <path d="M8 11h6"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                                <div className="address-info">
                                    <h4 className="address-name">{address.nameZh}</h4>
                                    {address.districtZh && (
                                        <p className="district-text">{address.districtZh}</p>
                                    )}
                                    <p className="address-text">{address.addressZh}</p>
                                </div>
                                
                                {/* QR Code for Chinese address */}
                                <div className="address-qr-section">
                                    <div className="address-qr-container">
                                        <QRCode
                                            value={chineseGoogleMapsUrl}
                                            size={80}
                                            level="M"
                                            includeMargin={true}
                                            className="address-qr-code"
                                        />
                                        <div className="address-qr-info">
                                            <a
                                                href={chineseGoogleMapsUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="address-maps-link"
                                                onClick={() => trackQRCodeInteraction('zh', 'maps_link')}
                                            >
                                                在 Google 地圖中開啟
                                            </a>
                                        </div>
                                    </div>
                                </div>
                                    </div>
                                )}
                                
                                {activeTab === 'english' && (
                                    <div className="address-section">
                                        <div className="section-header">
                                            <h3 className="section-title">English</h3>
                                    <div className="section-actions">
                                        <button
                                            className={`speech-button ${isReadingEnglish ? 'reading' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleReadEnglishAddress();
                                            }}
                                            aria-label="Read English address"
                                            title="Read English address"
                                        >
                                            <svg className="speech-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                                                <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
                                            </svg>
                                        </button>
                                        <button
                                            className={`copy-button ${copyFeedback === 'english' ? 'copied' : ''}`}
                                            onClick={handleCopyEnglishAddress}
                                            aria-label="Copy English address"
                                            title="Copy English address"
                                        >
                                            <svg className="copy-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                                            </svg>
                                        </button>
                                        <button
                                            className="zoom-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleZoomAddress('english');
                                            }}
                                            aria-label="Zoom English address"
                                            title="Zoom English address"
                                        >
                                            <svg className="zoom-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <circle cx="11" cy="11" r="8"/>
                                                <path d="m21 21-4.35-4.35"/>
                                                <path d="M11 8v6"/>
                                                <path d="M8 11h6"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                                <div className="address-info">
                                    <h4 className="address-name">{address.nameEn}</h4>
                                    {address.districtEn && (
                                        <p className="district-text">{address.districtEn}</p>
                                    )}
                                    <p className="address-text">{address.addressEn}</p>
                                </div>
                                
                                {/* QR Code for English address */}
                                <div className="address-qr-section">
                                    <div className="address-qr-container">
                                        <QRCode
                                            value={englishGoogleMapsUrl}
                                            size={80}
                                            level="M"
                                            includeMargin={true}
                                            className="address-qr-code"
                                        />
                                        <div className="address-qr-info">
                                            <a
                                                href={englishGoogleMapsUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="address-maps-link"
                                                onClick={() => trackQRCodeInteraction('en', 'maps_link')}
                                            >
                                                Open in Google Maps
                                            </a>
                                        </div>
                                    </div>
                                </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Zoom Modal */}
            {zoomedAddress && (
                <div className="zoom-overlay" onClick={handleCloseZoom}>
                    <div className={`zoom-modal ${zoomedAddress === 'chinese' ? 'chinese-theme' : 'english-theme'}`} onClick={(e) => e.stopPropagation()}>
                        <button className="zoom-close" onClick={handleCloseZoom}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 6L6 18M6 6l12 12"/>
                            </svg>
                        </button>
                        
                        <div className="zoom-content">
                            <div className="zoom-header">
                                <h2 className="zoom-title">
                                    {zoomedAddress === 'chinese' ? '中文地址' : 'English Address'}
                                </h2>
                            </div>
                            
                            <div className="zoom-address-info">
                                <h3 className="zoom-address-name">
                                    {zoomedAddress === 'chinese' ? address.nameZh : address.nameEn}
                                </h3>
                                {zoomedAddress === 'chinese' 
                                    ? (address.districtZh && (
                                        <p className="zoom-district-text">{address.districtZh}</p>
                                    ))
                                    : (address.districtEn && (
                                        <p className="zoom-district-text">{address.districtEn}</p>
                                    ))
                                }
                                <p className="zoom-address-text">
                                    {zoomedAddress === 'chinese' ? address.addressZh : address.addressEn}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(AddressDisplay); 